"use client";

import React, { useState } from "react";
import Link from "next/link";
import { ShieldCheck, Info, X } from "@phosphor-icons/react";
import { saveConsents } from "@/api";
import toast from "react-hot-toast";

const ConsentModal = ({ onAccept }) => {
  const [whatsappMarketing, setWhatsappMarketing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // All mandatory consents are automatically included
    const consents = [
      "privacy_policy",
      "terms_of_service",
      "personal_data",
      "academic_tracking",
      "third_party_processors"
    ];
    
    if (whatsappMarketing) {
      consents.push("whatsapp_marketing");
    }
    
    try {
      await saveConsents(consents);
      toast.success("Thank you! Your preferences have been saved.");
      if (onAccept) {
        onAccept();
      }
    } catch (error) {
      toast.error("Failed to save consent settings. Please try again.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/85 backdrop-blur-md p-4 overflow-y-auto">
      <div className="relative w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl animate-fade-in text-slate-100 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-slate-800 pb-4 flex-shrink-0">
          <div className="p-2 rounded-xl bg-blue-500/10 text-blue-400">
            <ShieldCheck size={28} />
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-white tracking-tight">Consent & Privacy Settings</h2>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mt-0.5">DPDPA 2023 Compliance Gate</p>
          </div>
        </div>

        {/* Scrollable Consent Items */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto my-6 pr-1 space-y-5">
          <p className="text-xs text-slate-400 leading-relaxed">
            In compliance with India's Digital Personal Data Protection (DPDP) Act 2023, we require your explicit consent to process personal details and deliver training resources. Please review the items below.
          </p>

          <div className="space-y-3.5">
            {/* Mandatory Item 1: Policy Agreement */}
            <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-slate-950/40 border border-slate-800/60">
              <input
                type="checkbox"
                checked={true}
                disabled={true}
                className="mt-1 w-4 h-4 rounded text-blue-600 bg-slate-800 border-slate-700 cursor-not-allowed"
              />
              <div className="flex-1">
                <span className="text-xs font-bold text-white flex items-center gap-1.5">
                  Agree to Policies <span className="text-[9px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.2 rounded-full uppercase">Required</span>
                </span>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                  I agree to the SOFAR <Link href="/terms-and-conditions" className="text-blue-400 hover:underline" target="_blank">Terms and Conditions</Link> (including our 7-day refund rules) and <Link href="/privacy-policy" className="text-blue-400 hover:underline" target="_blank">Privacy Policy</Link>.
                </p>
              </div>
            </div>

            {/* Mandatory Item 2: Personal Data */}
            <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-slate-950/40 border border-slate-800/60">
              <input
                type="checkbox"
                checked={true}
                disabled={true}
                className="mt-1 w-4 h-4 rounded text-blue-600 bg-slate-800 border-slate-700 cursor-not-allowed"
              />
              <div className="flex-1">
                <span className="text-xs font-bold text-white flex items-center gap-1.5">
                  Process Personal Data <span className="text-[9px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.2 rounded-full uppercase">Required</span>
                </span>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                  Consent to store and verify your name, email address, and phone number for secure login authentication.
                </p>
              </div>
            </div>

            {/* Mandatory Item 3: Progress Tracking */}
            <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-slate-950/40 border border-slate-800/60">
              <input
                type="checkbox"
                checked={true}
                disabled={true}
                className="mt-1 w-4 h-4 rounded text-blue-600 bg-slate-800 border-slate-700 cursor-not-allowed"
              />
              <div className="flex-1">
                <span className="text-xs font-bold text-white flex items-center gap-1.5">
                  Syllabus Progress Tracker <span className="text-[9px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.2 rounded-full uppercase">Required</span>
                </span>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                  Consent to track your video progress timestamps and completion ratios to show correct percentage values.
                </p>
              </div>
            </div>

            {/* Mandatory Item 4: AWS Services */}
            <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-slate-950/40 border border-slate-800/60">
              <input
                type="checkbox"
                checked={true}
                disabled={true}
                className="mt-1 w-4 h-4 rounded text-blue-600 bg-slate-800 border-slate-700 cursor-not-allowed"
              />
              <div className="flex-1">
                <span className="text-xs font-bold text-white flex items-center gap-1.5">
                  Third-Party Infrastructure (AWS) <span className="text-[9px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.2 rounded-full uppercase">Required</span>
                </span>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                  Consent to transfer encrypted streaming tokens to AWS S3 & CloudFront nodes to safeguard high-definition video feeds.
                </p>
              </div>
            </div>

            {/* Optional Item 5: WhatsApp Marketing */}
            <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800 hover:border-slate-700 transition-colors cursor-pointer" onClick={() => setWhatsappMarketing(!whatsappMarketing)}>
              <input
                type="checkbox"
                checked={whatsappMarketing}
                onChange={(e) => setWhatsappMarketing(e.target.checked)}
                className="mt-1 w-4 h-4 rounded text-blue-600 bg-slate-800 border-slate-700 focus:ring-blue-500/40 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="flex-1">
                <span className="text-xs font-bold text-white flex items-center gap-1.5">
                  WhatsApp Support & Alerts <span className="text-[9px] bg-slate-800 text-slate-400 border border-slate-700 px-1.5 py-0.2 rounded-full uppercase">Optional</span>
                </span>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                  Consent to send automated cohort schedules, course materials, batch notices, and customer support via WhatsApp. (You can withdraw this at any time).
                </p>
              </div>
            </div>
          </div>

          {/* DPDPA Notice footer */}
          <div className="flex gap-2 items-start bg-slate-950/60 p-3 rounded-2xl border border-slate-800 text-[10px] text-slate-500">
            <Info size={16} className="mt-0.5 text-slate-400 flex-shrink-0" />
            <p className="leading-relaxed">
              Consents are recorded with your current IP address and User-Agent signature. Under section 6(4) of the DPDP Act, you have the right to withdraw optional marketing consent at any time without affecting your core training syllabus access.
            </p>
          </div>

          {/* Action Button */}
          <div className="flex-shrink-0 pt-4 border-t border-slate-800">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold transition-all shadow-lg shadow-blue-600/20 active:scale-98 disabled:opacity-50 cursor-pointer text-center text-sm"
            >
              {isSubmitting ? "Saving Preferences..." : "Accept & Proceed"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConsentModal;
